import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Sparkles,
  TrendingDown,
  Clock,
  Target,
  FileText,
  School
} from 'lucide-react';
import ReplayButton from '../shared/ReplayButton';

const HeroSectionNew = ({ onStart, onAnimationComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);
  const [painPoint1Visible, setPainPoint1Visible] = useState(false);
  const [painPoint2Visible, setPainPoint2Visible] = useState(false);
  const [painPoint3Visible, setPainPoint3Visible] = useState(false);
  const [solutionVisible, setSolutionVisible] = useState(false);
  const [dashboardVisible, setDashboardVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [essaysCount, setEssaysCount] = useState(0);
  const [collegesCount, setCollegesCount] = useState(0);
  const [stressReduction, setStressReduction] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [focusCardRevealed, setFocusCardRevealed] = useState(false);
  const hasStarted = useRef(false);

  const startDemoAnimation = () => {
    // Reset all states
    setAnimationPhase(0);
    setPainPoint1Visible(false);
    setPainPoint2Visible(false);
    setPainPoint3Visible(false);
    setSolutionVisible(false);
    setDashboardVisible(false);
    setCtaVisible(false);
    setEssaysCount(0);
    setCollegesCount(0);
    setStressReduction(0);
    setCurrentProgress(0);
    setFocusCardRevealed(false);

    // Headline and first pain point appear immediately
    setTimeout(() => setAnimationPhase(1), 100);
    setTimeout(() => setPainPoint1Visible(true), 100);

    // SHORT PAUSE - Second pain point (800ms)
    setTimeout(() => setPainPoint2Visible(true), 800);

    // SHORT PAUSE - Third pain point (1600ms)
    setTimeout(() => setPainPoint3Visible(true), 1600);

    // PAUSE TO READ - Solution promise (3000ms - gives time to read all pain points)
    setTimeout(() => setSolutionVisible(true), 3000);

    // PAUSE - Dashboard appears (4200ms)
    setTimeout(() => {
      setDashboardVisible(true);
      setAnimationPhase(2);
    }, 4200);

    // Focus card reveals (5000ms)
    setTimeout(() => setFocusCardRevealed(true), 5000);

    // Numbers start animating (5500ms)
    setTimeout(() => {
      // Essays counter
      let essayInterval = setInterval(() => {
        setEssaysCount(prev => {
          if (prev >= 6) {
            clearInterval(essayInterval);
            return 6;
          }
          return prev + 1;
        });
      }, 100);

      // Colleges counter
      let collegeInterval = setInterval(() => {
        setCollegesCount(prev => {
          if (prev >= 12) {
            clearInterval(collegeInterval);
            return 12;
          }
          return prev + 1;
        });
      }, 80);

      // Stress reduction
      let stressInterval = setInterval(() => {
        setStressReduction(prev => {
          if (prev >= 63) {
            clearInterval(stressInterval);
            return 63;
          }
          return prev + 3;
        });
      }, 50);
    }, 5500);

    // Progress bar fills (6000ms)
    setTimeout(() => {
      let progressInterval = setInterval(() => {
        setCurrentProgress(prev => {
          if (prev >= 37) {
            clearInterval(progressInterval);
            return 37;
          }
          return prev + 1;
        });
      }, 30);
    }, 6000);

    // Smart unlock animation (6500ms)
    setTimeout(() => setAnimationPhase(3), 6500);

    // PAUSE - Show CTA with bouncing arrow (7000ms)
    setTimeout(() => setCtaVisible(true), 7000);

    // Complete animation
    setTimeout(() => {
      onAnimationComplete && onAnimationComplete();
    }, 8000);
  };

  useEffect(() => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      startDemoAnimation();
    }
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 relative">
      <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[600px]">

        {/* Left side - Copy */}
        <div className="space-y-8">
          {/* Headline */}
          <div className="space-y-4">
            <h1 className={`text-3xl lg:text-5xl font-bold transition-all duration-700 ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <span className="text-slate-900">The</span>{' '}
              <span className="text-teal-600">application </span>
              <span className="text-slate-900">process usually looks something like this...</span>
            </h1>
          </div>

          {/* Pain points */}
          <div className="space-y-4">
            <div className={`bg-white rounded-xl p-5 border-2 border-slate-200 shadow-sm transition-all duration-500 ${
              painPoint1Visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}>
              <div className="flex items-start gap-3">
                <div className="text-2xl">😵</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1">Everything feels urgent</div>
                  <div className="text-sm text-slate-600">47 different deadlines, 12 essays to write, test scores to submit...</div>
                </div>
              </div>
            </div>

            <div className={`bg-white rounded-xl p-5 border-2 border-slate-200 shadow-sm transition-all duration-500 ${
              painPoint2Visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}>
              <div className="flex items-start gap-3">
                <div className="text-2xl">📝</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1">Your essays sound like everyone else's</div>
                  <div className="text-sm text-slate-600">"I learned the value of hard work" could be written by anyone</div>
                </div>
              </div>
            </div>

            <div className={`bg-white rounded-xl p-5 border-2 border-slate-200 shadow-sm transition-all duration-500 ${
              painPoint3Visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}>
              <div className="flex items-start gap-3">
                <div className="text-2xl">🎯</div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 mb-1">You're applying everywhere with no real strategy</div>
                  <div className="text-sm text-slate-600">Throwing apps at random schools without understanding your actual fit</div>
                </div>
              </div>
            </div>
          </div>

          {/* Solution promise */}
          <div className={`bg-teal-50 rounded-xl p-6 border-2 border-teal-200 transition-all duration-700 ${
            solutionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <p className="text-slate-700 leading-relaxed text-xl">
              Let's transform the chaos into manageable pieces that actually get you somewhere.
            </p>
          </div>

          {/* CTA */}
          <div className={`flex items-center gap-4 transition-all duration-700 ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <button
              onClick={onStart}
              className="group px-8 py-4 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-semibold text-lg flex items-center gap-3 shadow-lg"
            >
              Show me how this works
              <ArrowRight className={`w-5 h-5 transition-transform ${
                ctaVisible ? 'animate-bounce-horizontal' : ''
              }`} />
            </button>
            {animationPhase >= 3 && (
              <ReplayButton onClick={startDemoAnimation} />
            )}
            <span className="text-sm text-slate-500">
              5-minute walkthrough
            </span>
          </div>
        </div>

        {/* Right side - Dashboard Preview */}
        <div className={`relative transition-all duration-1000 ${
          dashboardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200 overflow-hidden">
            {/* Dashboard header */}
            <div className="bg-gradient-to-r from-slate-50 to-teal-50 px-6 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Today's Focus</h3>
                <span className={`px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full transition-all duration-500 ${
                  focusCardRevealed ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                }`}>
                  On Track
                </span>
              </div>
            </div>

            {/* Main focus card */}
            <div className="p-6">
              <div className={`bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-6 mb-6 transition-all duration-700 transform ${
                focusCardRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 mb-1">Create your first draft using your strategic profile</h4>
                    <p className="text-sm text-slate-600 mb-3">2.5h work session</p>

                    {/* Smart unlock banner */}
                    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-3 transition-all duration-500 ${
                      animationPhase >= 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          High impact milestone
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Current progress</span>
                  <span className={`text-sm text-slate-600 transition-all duration-300 ${
                    currentProgress > 0 ? 'opacity-100' : 'opacity-0'
                  }`}>
                    4 of 14 sessions complete
                  </span>
                </div>

                {/* Progress bar with gradient */}
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-red-400 via-yellow-400 via-emerald-400 to-azure-500 transition-all duration-1000 ease-out"
                    style={{ width: `${currentProgress}%` }}
                  />
                </div>
              </div>

              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className={`text-center transition-all duration-500 delay-100 ${
                  essaysCount > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <div className="text-2xl font-bold text-slate-900">{essaysCount}</div>
                  <div className="text-xs text-slate-600">Essays drafted</div>
                </div>
                <div className={`text-center transition-all duration-500 delay-200 ${
                  collegesCount > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <div className="text-2xl font-bold text-slate-900">{collegesCount}</div>
                  <div className="text-xs text-slate-600">Colleges researched</div>
                </div>
                <div className={`text-center transition-all duration-500 delay-300 ${
                  stressReduction > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <div className="text-2xl font-bold text-teal-600">↓{stressReduction}%</div>
                  <div className="text-xs text-slate-600">Stress level</div>
                </div>
              </div>

              {/* Bottom section - shows organization */}
              <div className={`mt-6 pt-6 border-t border-slate-200 transition-all duration-700 delay-500 ${
                animationPhase >= 3 ? 'opacity-100' : 'opacity-0'
              }`}>
                <p className="text-sm text-slate-600 mb-3">8 focused sessions completed</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-600" />
                  <span className="text-sm font-medium text-teal-700">
                    {stressReduction > 50 ? '34%' : `${100 - stressReduction}%`} stress level
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for bouncing arrow animation */}
      <style jsx>{`
        @keyframes bounce-horizontal {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
        }

        .animate-bounce-horizontal {
          animation: bounce-horizontal 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HeroSectionNew;